import React from "react";
import { InputMode, ResponseMode } from "./types";
import { inputModes, responseModes } from "./data";

interface ModeSelectorProps {
  selectedInputMode: InputMode;
  selectedResponseMode: ResponseMode;
  onInputModeChange: (mode: InputMode) => void;
  onResponseModeChange: (mode: ResponseMode) => void;
}

export const ModeSelector: React.FC<ModeSelectorProps> = ({
  selectedInputMode,
  selectedResponseMode,
  onInputModeChange,
  onResponseModeChange,
}) => {
  const getSelectedInputMode = () =>
    inputModes.find((mode) => mode.value === selectedInputMode);
  const getSelectedResponseMode = () =>
    responseModes.find((mode) => mode.value === selectedResponseMode);

  return (
    <div className="flex flex-col space-y-6 ">
      {/* Input Mode Section */}
      <div>
        <div className="mb-4">
          <h3 className="mb-2 text-sm font-medium text-gray-700">Input Mode</h3>
          <p className="mb-3 text-xs text-gray-500">
            Choose how you want to communicate with the AI trainer
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap p-1 space-x-1 bg-gray-100 rounded-lg">
            {inputModes.map((mode) => (
              <button
                key={mode.value}
                onClick={() => onInputModeChange(mode.value)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 flex-1 justify-center ${
                  selectedInputMode === mode.value
                    ? "bg-white text-blue-600 shadow-xs"
                    : "text-gray-600 hover:text-gray-800 hover:bg-white/50"
                }`}
                aria-pressed={selectedInputMode === mode.value}
              >
                <span className="hidden sm:block">{mode.icon}</span>
                <span className=" whitespace-nowrap">{mode.label}</span>
              </button>
            ))}
          </div>

          <div className="hidden p-3 border border-blue-100 rounded-lg bg-blue-50 lg:block">
            <div className="text-xs text-blue-800">
              <div className="mb-1 font-medium">
                {getSelectedInputMode()?.label} Mode
              </div>
              <div>{getSelectedInputMode()?.description}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Response Mode Section */}
      <div>
        <div className="mb-4">
          <h3 className="mb-2 text-sm font-medium text-gray-700">
            Response Mode
          </h3>
          <p className="mb-3 text-xs text-gray-500">
            Choose how the AI trainer should respond to you
          </p>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap p-1 space-x-1 bg-gray-100 rounded-lg">
            {responseModes.map((mode) => (
              <button
                key={mode.value}
                onClick={() => onResponseModeChange(mode.value)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 flex-1 justify-center ${
                  selectedResponseMode === mode.value
                    ? "bg-white text-green-600 shadow-xs"
                    : "text-gray-600 hover:text-gray-800 hover:bg-white/50"
                }`}
                aria-pressed={selectedResponseMode === mode.value}
              >
                <span className="hidden sm:block">{mode.icon}</span>
                <span className=" whitespace-nowrap">{mode.label}</span>
              </button>
            ))}
          </div>

          <div className="hidden p-3 border border-green-100 rounded-lg bg-green-50 lg:block">
            <div className="text-xs text-green-800">
              <div className="mb-1 font-medium">
                {getSelectedResponseMode()?.label} Response
              </div>
              <div>{getSelectedResponseMode()?.description}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
